import { useState, useEffect, useCallback } from 'react';
import { useTestContext } from '../../../context/testContext';
import { useCalibration } from '../../../context/calibrationContext';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { Button, Flex, LogmarSelector, ResponsiveAcuityInfoPanel, Text, theme } from '../../../2022-component-library';
import { getLogmarLineSize500mm } from '../../../helpers/functions';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useLogmarData } from '../../../helpers/reducers';

const ResponsiveLogmarTestOptician = ({ setCanProgress, eyeTested, logmarType }) => {
  const {
    calibrationData: { distanceFromScreen, pixelDensity, methodSelected },
    updateCalibrationData,
  } = useCalibration();
  const { testData, logmarCharacters, updateTestData, generateLogmarCharacters } = useTestContext();
  const [charsSeen, setCharsSeen] = useState();
  const [currentLogmar, setCurrentLogmar] = useState(testData?.eyeTested);
  const [testMode, setTestMode] = useState(methodSelected !== 'manual' ? 'auto' : testData.testMode);
  const messageFromPatient = useReceiveMessage();
  const postMessage = usePostMessage();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [testEnded, setTestEnded] = useState(false);
  const { patientDistanceReference, pixelDensityReference } = calibrationConstants;
  const { currentLogmarData, updateLogmarData } = useLogmarData();

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const endTest = useCallback(() => {
    updateTestData({ [eyeTested]: { score: currentLogmarData.finalScore } });
    return setTestEnded(true);
  }, [currentLogmarData.finalScore, eyeTested, updateTestData]);

  const selectPreviousLogmar = () => {
    generateLogmarCharacters();
    return updateLogmarData({ type: 'incrementLogmarSize', payload: currentLogmar });
  };

  const selectNextLogmar = useCallback(() => {
    setCharsSeen(undefined);
    if (currentLogmar.id === 13) return endTest();
    return updateLogmarData({ type: 'decrementLogmarSize' });
  }, [currentLogmar.id, endTest, updateLogmarData]);

  const getLogmarFontSize = useCallback(
    (logmar, distance) => {
      let lineSize;
      if (patientDistanceReference === 500) {
        lineSize = getLogmarLineSize500mm(logmar.id);
      }
      const fontSize = (lineSize * distance) / patientDistanceReference / pixelDensity;
      const fontSizeAdjustment = (pixelDensity * testData.fontAdjustmentPercentage) / pixelDensityReference / 100;
      const adjustedFontSize = fontSize + fontSize * fontSizeAdjustment;
      return adjustedFontSize;
    },
    [patientDistanceReference, pixelDensity, pixelDensityReference, testData?.fontAdjustmentPercentage],
  );

  const updateLogmarFontSize = useCallback(
    (logmar, distance) => {
      const fontSize = getLogmarFontSize(logmar, distance);
      postMessage({
        type: 'resizeLogmar',
        content: {
          fontSize: Math.round(fontSize),
          distanceReferenceInMM: distance,
          logmarCharacters: logmarCharacters[logmar.id],
        },
      });
    },
    [getLogmarFontSize, logmarCharacters, postMessage],
  );

  useEffect(() => {
    updateLogmarFontSize(currentLogmarData.logmarSize, distanceFromScreen);
  }, [currentLogmarData.logmarSize, distanceFromScreen, updateLogmarFontSize]);

  useEffect(() => {
    setCurrentLogmar(currentLogmarData.logmarSize);
  }, [currentLogmarData.logmarSize]);

  const changeTestMode = useCallback(
    (selectedTestMode) => {
      if (selectedTestMode !== testMode) {
        updateTestData({ testMode: selectedTestMode });
        setTestMode(selectedTestMode);
        postMessage({
          type: 'updateTestMode',
          content: {
            testMode: selectedTestMode,
          },
        });
      }
    },
    [testMode, updateTestData, postMessage],
  );

  useEffect(() => {
    if (isInitialLoad === true) {
      updateLogmarData({ type: 'setInitialLogmarSize', payload: testData?.eyeTested || {} });
      postMessage({
        type: 'visualAcuityTest',
        content: {
          fontSize: Math.round(getLogmarFontSize(currentLogmar, distanceFromScreen)),
          distanceFromScreen: distanceFromScreen,
          logmarCharacters: logmarCharacters[currentLogmar.id],
          logmarType: logmarType,
          methodSelected: methodSelected,
          testMode: testMode,
        },
      });
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  useEffect(() => {
    if (testEnded) setCanProgress(true);
  }, [testEnded, setCanProgress]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientMovementDetected') {
      const { patientDistance, patientIsMoving } = messageFromPatient.content;
      updateCalibrationData({ distanceFromScreen: patientDistance });

      if (patientIsMoving === false) {
        updateLogmarFontSize(currentLogmar, patientDistance);
      }
    }
  }, [messageFromPatient, currentLogmar, updateCalibrationData, updateLogmarFontSize]);

  useEffect(() => {
    if (charsSeen !== undefined) {
      const multiplier = testData.lettersToUse === 'sloan' ? 0.02 : 0.025;
      const amountToSubtractFromScore = charsSeen * multiplier;
      updateLogmarData({ type: 'adjustFinalScore', payload: amountToSubtractFromScore });
      selectNextLogmar();
    }
  }, [charsSeen, updateLogmarData, selectNextLogmar, testData.lettersToUse]);

  return (
    <Flex centered column width='100%'>
      <Flex width='100%' padding='0.5rem' paddingRight='1.5rem' justifyContent='space-between'>
        <LogmarSelector
          logmarCharacters={logmarCharacters}
          selectedLogmar={currentLogmar}
          setSelectedLogmar={setCurrentLogmar}
          marginBottom='1rem'
          width='65vw'
          height='40vh'
          onClick={() => {
            return false;
          }}
          testInProgress
        />
        <Flex column centered width='10vw' minWidth='150px' marginLeft='3rem'>
          {testEnded === true ? (
            <Text textAlign='center' fontSize='0.75rem' width='100%' marginBottom='0.5rem'>
              Test has ended
            </Text>
          ) : (
            <>
              <Button
                width='13em'
                marginBottom='1rem'
                onClick={() => selectPreviousLogmar()}
                disabled={currentLogmar.id === 1 || currentLogmar.id === testData.maxPermittedLogmar.id}
              >
                {`Previous logMAR`}
              </Button>
              <Text textAlign='center' fontSize='0.75rem' width='100%' marginBottom='0.5rem'>
                How many characters seen?
              </Text>
              <Flex>
                {[0, 1, 2].map((num) => (
                  <Button
                    key={`chars-seen-btn-${num}`}
                    backgroundColor={num === 0 && theme.colors.negativeDark}
                    width='3.5rem'
                    marginLeft='0.3rem'
                    marginRight='0.3rem'
                    onClick={() => {
                      return num === 0 ? endTest() : setCharsSeen(num);
                    }}
                  >
                    {num}
                  </Button>
                ))}
              </Flex>
              <Flex marginTop='10px'>
                {[3, 4, 5].map((num) => {
                  if (testData.lettersToUse === 'glasgow' && num === 5) return null;
                  return (
                    <Button key={`chars-seen-btn-${num}`} width='3.5rem' marginLeft='0.3rem' marginRight='0.3rem' onClick={() => setCharsSeen(num)}>
                      {num}
                    </Button>
                  );
                })}
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      <ResponsiveAcuityInfoPanel changeTestMode={changeTestMode} />
    </Flex>
  );
};

export default ResponsiveLogmarTestOptician;
